// src/lib/managers/contact/contactName.ts

/**
 * F-1.13 — un contact correspond-il au nom demandé ?
 *
 * La recherche de contacts (RAG) rend jusqu'à 20 voisins sémantiques, sans seuil : pour
 * « patrick triaud », aussi « vincent.triaud » ou « thibaut.monnier ». Un candidat ne correspond
 * que si CHAQUE mot du nom demandé se retrouve dans son nom affiché ou dans la partie locale de son
 * adresse : à l'identique, en début de mot (« pat » → « patrick »), ou à une faute de frappe près
 * pour un mot d'au moins 5 lettres (« triaux » → « triaud »). Accents et casse ignorés ; le domaine
 * de l'adresse n'est pas lu (une organisation se cherche autrement : voir les appelants).
 */

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PREFIX_MIN = 3;
const TYPO_MIN = 5;
/** Longueur à partir de laquelle une faute de frappe est permise pour PROPOSER un contact. */
const SUGGESTION_TYPO_MIN = 3;
/**
 * Longueur à partir de laquelle un mot seul peut être l'abréviation d'un contact, pour le PROPOSER
 * (« pj » → Paul Jourdan) : CONCEPTION-noms-courts.md de l'Api-Ai, décision de PJ du 15/10.
 */
const ABBREVIATION_MIN = 2;

/** Vrai si le texte est une adresse mail (pas un nom à chercher). */
export const isEmailAddress = (value: string): boolean =>
    EMAIL_RE.test(value.trim());

/** Mots d'un nom : minuscules, sans accents, coupés sur tout ce qui n'est ni lettre ni chiffre. */
export const nameTokens = (text: string): string[] =>
    text
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length > 0);

/** Distance d'édition bornée à 1 (au plus une lettre ajoutée, retirée ou changée). */
const withinOneEdit = (a: string, b: string): boolean => {
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            i++;
            j++;
        } else {
            edits++;
            if (edits > 1) return false;
            if (a.length > b.length) i++;
            else if (b.length > a.length) j++;
            else {
                i++;
                j++;
            }
        }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
};

const tokenMatches = (
    wanted: string,
    candidate: string,
    typoMin: number = TYPO_MIN,
): boolean =>
    wanted === candidate ||
    (wanted.length >= PREFIX_MIN && candidate.startsWith(wanted)) ||
    (wanted.length >= typoMin &&
        candidate.length >= typoMin &&
        withinOneEdit(wanted, candidate));

const everyTokenMatches = (
    wantedTokens: string[],
    displayName: string,
    email: string,
    typoMin: number,
): boolean => {
    if (wantedTokens.length === 0) return false;
    const candidateTokens = [
        ...nameTokens(displayName),
        ...nameTokens(email.split("@")[0] ?? ""),
    ];
    return wantedTokens.every((wanted) =>
        candidateTokens.some((candidate) =>
            tokenMatches(wanted, candidate, typoMin),
        ),
    );
};

/** Vrai si chaque mot du nom demandé se retrouve dans le nom affiché ou l'adresse du contact. */
export const matchesName = (
    wantedTokens: string[],
    displayName: string,
    email: string,
): boolean => everyTokenMatches(wantedTokens, displayName, email, TYPO_MIN);

/** Les initiales du nom affiché, dans l'ordre : « Paul Jourdan » → « pj ». */
const initialsOf = (displayName: string): string =>
    nameTokens(displayName)
        .map((token) => token[0])
        .join("");

/**
 * Vrai si un nom d'un seul mot, dès 2 lettres, est une abréviation du contact : le début de ses
 * initiales (« pj » → Paul Jourdan), ou le début d'un mot de son nom affiché ou de son adresse
 * (« pj » → pjourdan@…). Assez sûr pour proposer ; jamais pour choisir.
 */
const abbreviates = (
    wantedTokens: string[],
    displayName: string,
    email: string,
): boolean => {
    if (wantedTokens.length !== 1) return false;
    const [wanted] = wantedTokens;
    if (wanted.length < ABBREVIATION_MIN) return false;
    if (initialsOf(displayName).startsWith(wanted)) return true;
    return [
        ...nameTokens(displayName),
        ...nameTokens(email.split("@")[0] ?? ""),
    ].some((candidate) => candidate.startsWith(wanted));
};

/**
 * Vrai si le contact RESSEMBLE au nom demandé sans y correspondre : la même règle que `matchesName`,
 * la faute de frappe permise dès 3 lettres (« jan » → « jean », « marie-ane » → « Marie-Anne ») ; ou
 * le nom, d'un seul mot, en est une abréviation (« pj » → Paul Jourdan, 15/10).
 * Trop large pour choisir un contact à la place de l'utilisateur ; assez sûr pour le lui proposer
 * (« Vouliez-vous dire … ? ») quand le nom est introuvable.
 */
export const resemblesName = (
    wantedTokens: string[],
    displayName: string,
    email: string,
): boolean =>
    (everyTokenMatches(wantedTokens, displayName, email, SUGGESTION_TYPO_MIN) ||
        abbreviates(wantedTokens, displayName, email)) &&
    !matchesName(wantedTokens, displayName, email);

/**
 * Vrai si chaque mot du nom demandé est un mot entier du nom affiché ou de l'adresse : « marc » est
 * exact pour « marc.dupont », pas pour « marcel.durand » (début de mot) ni « mark » (faute).
 */
const matchesNameExactly = (
    wantedTokens: string[],
    displayName: string,
    email: string,
): boolean => {
    if (wantedTokens.length === 0) return false;
    const candidateTokens = new Set([
        ...nameTokens(displayName),
        ...nameTokens(email.split("@")[0] ?? ""),
    ]);
    return wantedTokens.every((wanted) => candidateTokens.has(wanted));
};

/**
 * Candidats compatibles, ceux dont le nom correspond mot pour mot d'abord (ordre d'origine gardé
 * dans chaque groupe) ; `best` : le groupe du premier, où se juge l'ambiguïté.
 */
export const exactFirst = <T extends { email: string; displayName: string }>(
    wantedTokens: string[],
    candidates: T[],
): { ranked: T[]; best: T[] } => {
    const exact = candidates.filter((c) =>
        matchesNameExactly(wantedTokens, c.displayName, c.email),
    );
    const others = candidates.filter((c) => !exact.includes(c));
    return {
        ranked: [...exact, ...others],
        best: exact.length > 0 ? exact : others,
    };
};
